(() => {
  const instances = new WeakMap();
  const ROOT_SELECTOR = '[data-infinite-product-scroll]';
  const GRID_CONTAINER_SELECTOR = '#ProductGridContainer';
  const PRODUCT_GRID_SELECTOR = '#product-grid.product-grid, #product-grid .product-grid';
  const CARD_SELECTOR = ':scope > .grid__item';
  const PAGINATION_SELECTOR = '.pagination-wrapper';
  const NEXT_LINK_SELECTOR = '[data-pagination-next]';
  const OBSERVER_MARGIN = '800px 0px';
  const READY_CLASS = 'infinite-scroll-ready';

  class InfiniteProductScroll {
    constructor(root) {
      this.root = root;
      this.container = root.querySelector(GRID_CONTAINER_SELECTOR);
      this.grid = this.container?.querySelector(PRODUCT_GRID_SELECTOR);
      this.controls = root.querySelector('[data-infinite-product-scroll-controls]');
      this.status = root.querySelector('[data-infinite-product-scroll-status]');
      this.loader = root.querySelector('[data-infinite-product-scroll-loader]');
      this.error = root.querySelector('[data-infinite-product-scroll-error]');
      this.retryButton = root.querySelector('[data-infinite-product-scroll-retry]');
      this.manualButton = root.querySelector('[data-infinite-product-scroll-manual]');
      this.sentinel = root.querySelector('[data-infinite-product-scroll-sentinel]');
      this.backToTopButton = root.querySelector('[data-infinite-product-scroll-back-to-top]');
      this.loadedUrls = new Set();
      this.abortController = null;
      this.isLoading = false;
      this.hasEnded = false;

      if (!this.container || !this.grid || !this.controls || !this.status) return;

      this.totalProducts = Number(this.controls.dataset.totalProducts || 0);
      this.nextUrl = this.getNextUrl(document);
      this.onRetry = () => this.loadNextPage();
      this.onManual = () => this.loadNextPage();
      this.onBackToTop = () => this.scrollToTop();
      this.onScroll = () => this.updateBackToTop();
      this.retryButton?.addEventListener('click', this.onRetry);
      this.manualButton?.addEventListener('click', this.onManual);
      this.backToTopButton?.addEventListener('click', this.onBackToTop);
      window.addEventListener('scroll', this.onScroll, { passive: true });

      this.root.classList.add(READY_CLASS);
      this.updateStatus();
      this.setupObserver();
      this.updateBackToTop();
    }

    setupObserver() {
      // Observer setup: load the next native Shopify pagination URL shortly before the sentinel is visible.
      if (!this.nextUrl) {
        this.end();
        return;
      }

      if (!('IntersectionObserver' in window) || !this.sentinel) {
        this.manualButton.hidden = false;
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
        },
        { rootMargin: OBSERVER_MARGIN },
      );
      this.observer.observe(this.sentinel);
    }

    getProductCards(scope = this.grid) {
      return scope ? Array.from(scope.querySelectorAll(CARD_SELECTOR)) : [];
    }

    getNextUrl(doc) {
      // Pagination selector: use Shopify's server-rendered next link rather than constructing page URLs.
      const href = doc.querySelector(`${ROOT_SELECTOR} ${PAGINATION_SELECTOR} ${NEXT_LINK_SELECTOR}`)?.getAttribute('href');
      return href ? new URL(href, window.location.origin).href : '';
    }

    getCardKey(card) {
      const link = card.querySelector('a[href*="/products/"]') || card.querySelector('a[href]');
      return link ? new URL(link.getAttribute('href'), window.location.origin).pathname : card.textContent.trim();
    }

    updateStatus(message) {
      const loaded = this.getProductCards().length;
      const total = this.totalProducts || loaded;
      this.status.textContent = message || (loaded >= total && !this.nextUrl ? `All ${total} products loaded` : `Showing ${loaded} of ${total} products`);
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      if (this.loader) this.loader.hidden = !isLoading;
      if (isLoading && this.error) this.error.hidden = true;
    }

    async loadNextPage() {
      // AJAX fetch: request one Shopify pagination page, parse its product cards, and append only new cards.
      if (this.isLoading || this.hasEnded || !this.nextUrl || this.loadedUrls.has(this.nextUrl)) return;

      const requestUrl = this.nextUrl;
      this.loadedUrls.add(requestUrl);
      this.setLoading(true);
      this.abortController = new AbortController();

      try {
        const response = await fetch(requestUrl, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(`Infinite product scroll failed: ${response.status} ${response.statusText}`);

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = parsed.querySelector(`${GRID_CONTAINER_SELECTOR} ${PRODUCT_GRID_SELECTOR}`);
        const nextCards = this.getProductCards(nextGrid);

        if (!nextGrid || nextCards.length === 0) throw new Error('Infinite product scroll could not find product cards on the next page.');

        const existingKeys = new Set(this.getProductCards().map((card) => this.getCardKey(card)));
        nextCards.forEach((card) => {
          const key = this.getCardKey(card);
          if (!existingKeys.has(key)) {
            this.grid.appendChild(document.importNode(card, true));
            existingKeys.add(key);
          }
        });

        this.nextUrl = this.getNextUrl(parsed);
        this.setLoading(false);
        this.updateStatus();

        if (!this.nextUrl) this.end();
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        this.setLoading(false);
        if (this.error) this.error.hidden = false;
        if (this.manualButton) this.manualButton.hidden = false;
        this.loadedUrls.delete(requestUrl);
      }
    }

    end() {
      this.hasEnded = true;
      this.observer?.disconnect();
      if (this.manualButton) this.manualButton.hidden = true;
      this.updateStatus();
    }

    updateBackToTop() {
      if (!this.backToTopButton) return;
      const rootTop = this.root.getBoundingClientRect().top + window.scrollY;
      this.backToTopButton.hidden = window.scrollY < rootTop + 900;
    }

    scrollToTop() {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    }

    destroy() {
      // Filter reset behaviour: discard pending old pagination requests when facets replace the grid.
      this.observer?.disconnect();
      this.abortController?.abort();
      this.retryButton?.removeEventListener('click', this.onRetry);
      this.manualButton?.removeEventListener('click', this.onManual);
      this.backToTopButton?.removeEventListener('click', this.onBackToTop);
      window.removeEventListener('scroll', this.onScroll);
      this.root.classList.remove(READY_CLASS);
    }
  }

  function initialiseInfiniteProductScroll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      instances.get(root)?.destroy();
      const instance = new InfiniteProductScroll(root);
      instances.set(root, instance);
    });
  }

  document.addEventListener('DOMContentLoaded', initialiseInfiniteProductScroll);
  document.addEventListener('shopify:section:load', initialiseInfiniteProductScroll);
  document.addEventListener('collection:product-grid:updated', initialiseInfiniteProductScroll);
})();
